<script>
const SLIDER_MAX = 300;

function lerp(a, b, t) { return a + (b - a) * t; }

export default {
  name: 'ModalPreviewCrop',

  props: {
    cut:      { type: Object, default: null },
    videoUrl: { type: String, default: null },
  },

  data() {
    return {
      showModal: false,
      keyframes:         [],   // [{ time, x_offset }] — read-only, from cut.crop_stats
      baseOffset:        0,
      isPlaying:         false,
      currentTime:       0,
      playbackSpeed:     1,
      playCheckInterval: null,
      videoNaturalW:     0,
      videoNaturalH:     0,
      containerW:        0,
      containerH:        0,
    };
  },

  computed: {
    cutStart() {
      if (!this.cut) return 0;
      return this.cut.clip_start_sec != null ? this.cut.clip_start_sec : (this.cut.start || 0);
    },
    cutEnd() {
      if (!this.cut) return 0;
      return this.cut.clip_end_sec != null ? this.cut.clip_end_sec : (this.cut.end || 0);
    },
    cutDuration() {
      return Math.max(this.cutEnd - this.cutStart, 0.001);
    },
    relativeTime() {
      return Math.max(0, this.currentTime - this.cutStart);
    },
    cropBoxWidthPct() { return (81 / 256) * 100; },

    videoContentArea() {
      const vW = this.videoNaturalW, vH = this.videoNaturalH;
      const cW = this.containerW,   cH = this.containerH;
      if (!vW || !vH || !cW || !cH) return null;
      const scale = Math.min(cW / vW, cH / vH);
      const w = vW * scale, h = vH * scale;
      return {
        position: 'absolute',
        left:   Math.round((cW - w) / 2) + 'px',
        top:    Math.round((cH - h) / 2) + 'px',
        width:  Math.round(w) + 'px',
        height: Math.round(h) + 'px',
      };
    },
  },

  beforeDestroy() {
    this.stopPreview();
  },

  methods: {
    open() {
      this._loadCrop();
      this.showModal = true;
    },
    close() { this.stopPreview(); this.showModal = false; },

    onModalShown() {
      this._loadCrop();
      this.seekToStart();
      this.$nextTick(() => {
        this.updateContainerSize();
        this.applyVisualLeft(this.interpolateAt(0));
        this._syncScrubThumb(this.currentTime);
      });
      this._resizeHandler = () => this.updateContainerSize();
      window.addEventListener('resize', this._resizeHandler);
    },

    onModalHidden() {
      this.stopPreview();
      window.removeEventListener('resize', this._resizeHandler);
      this.$emit('hidden');
    },

    _loadCrop() {
      const cs = this.cut && this.cut.crop_stats;
      if (cs && Array.isArray(cs.keyframes) && cs.keyframes.length) {
        this.keyframes  = cs.keyframes.map(k => ({ ...k }));
        this.baseOffset = this.interpolateAt(0);
      } else if (cs && cs.x_offset !== undefined) {
        this.keyframes  = [];
        this.baseOffset = cs.x_offset;
      } else {
        this.keyframes  = [];
        this.baseOffset = 0;
      }
    },

    onVideoLoadedMetadata() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this.videoNaturalW = v.videoWidth;
      this.videoNaturalH = v.videoHeight;
      this.$nextTick(() => {
        this.updateContainerSize();
        this.applyVisualLeft(this.interpolateAt(0));
      });
    },

    updateContainerSize() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this.containerW = v.clientWidth;
      this.containerH = v.clientHeight;
    },

    // ── Crop region (masks) ───────────────────────────────────
    applyVisualLeft(xOffset) {
      const half = (100 - this.cropBoxWidthPct) / 2;
      const clamped = Math.min(Math.max(xOffset, -SLIDER_MAX), SLIDER_MAX);
      const leftPct = half + (clamped / SLIDER_MAX) * half;
      const rightStartPct = leftPct + this.cropBoxWidthPct;
      const box = this.$refs.cropBox;
      if (box) box.style.left = leftPct + '%';
      const ml = this.$refs.maskL;
      if (ml) ml.style.width = leftPct + '%';
      const mr = this.$refs.maskR;
      if (mr) mr.style.left = rightStartPct + '%';
    },

    interpolateAt(relT) {
      const kfs = [...this.keyframes].sort((a, b) => a.time - b.time);
      if (!kfs.length)      return this.baseOffset;
      if (kfs.length === 1) return kfs[0].x_offset;
      if (relT <= kfs[0].time) return kfs[0].x_offset;
      if (relT >= kfs[kfs.length - 1].time) return kfs[kfs.length - 1].x_offset;
      for (let i = 0; i < kfs.length - 1; i++) {
        if (relT >= kfs[i].time && relT <= kfs[i + 1].time) {
          const t = (relT - kfs[i].time) / (kfs[i + 1].time - kfs[i].time);
          return lerp(kfs[i].x_offset, kfs[i + 1].x_offset, t);
        }
      }
      return this.baseOffset;
    },

    // ── Video ─────────────────────────────────────────────────
    seekToStart() {
      const v = this.$refs.previewVideo;
      if (v && this.cut) { v.currentTime = this.cutStart; this.currentTime = this.cutStart; this._syncScrubThumb(this.cutStart); }
    },

    seekTo(t) {
      const v = this.$refs.previewVideo;
      if (v) { v.currentTime = t; this.currentTime = t; this._syncScrubThumb(t); this.applyVisualLeft(this.interpolateAt(Math.max(0, t - this.cutStart))); }
    },

    _syncScrubThumb(t) {
      const s = this.$refs.scrubSlider;
      if (s) s.value = String(t);
    },

    onScrubInput(e) {
      this._scrubbing = true;
      this.seekTo(+e.target.value);
    },
    onScrubChange() { this._scrubbing = false; },

    setPlaybackSpeed(speed) {
      this.playbackSpeed = speed;
      const v = this.$refs.previewVideo;
      if (v) v.playbackRate = speed;
    },

    playPreview() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this._scrubbing = false;
      v.currentTime  = this.cutStart;
      v.playbackRate = this.playbackSpeed;
      v.play();
      this.playCheckInterval = setInterval(() => {
        if (!this.$refs.previewVideo) return;
        this.currentTime = this.$refs.previewVideo.currentTime;
        if (this.$refs.previewVideo.currentTime >= this.cutEnd) this.stopPreview();
      }, 80);
    },

    stopPreview() {
      clearInterval(this.playCheckInterval);
      this.playCheckInterval = null;
      const v = this.$refs.previewVideo;
      if (v) v.pause();
    },

    onVideoPlay()  { this.isPlaying = true; },
    onVideoPause() { this.isPlaying = false; },
    onVideoEnded() { this.isPlaying = false; clearInterval(this.playCheckInterval); },
    onVideoTimeUpdate() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this.currentTime = v.currentTime;
      if (!this._scrubbing) this._syncScrubThumb(v.currentTime);
      const relT = Math.max(0, v.currentTime - this.cutStart);
      this.applyVisualLeft(this.interpolateAt(relT));
    },

    formatSecs(secs) {
      if (secs == null || isNaN(secs)) return '0:00';
      const s = Math.floor(Number(secs));
      return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    },
    formatSecsDecimal(secs) {
      if (secs == null || isNaN(secs)) return '0:00.0';
      const n = Number(secs);
      const m = Math.floor(n / 60);
      const s = (n % 60).toFixed(1).padStart(4, '0');
      return `${m}:${s}`;
    },
  },
};
</script>

<template>
  <b-modal
    id="modal-preview-crop"
    v-model="showModal"
    size="xl"
    title="Preview Crop"
    title-class="font-18"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <template v-if="cut">

      <!-- ── Video with crop preview masks ── -->
      <div class="cpv-video-wrap mb-2">
        <video
          v-if="videoUrl"
          ref="previewVideo"
          class="cpv-video"
          :src="videoUrl"
          preload="auto"
          @play="onVideoPlay"
          @pause="onVideoPause"
          @ended="onVideoEnded"
          @timeupdate="onVideoTimeUpdate"
          @loadedmetadata="onVideoLoadedMetadata"
        ></video>
        <div v-else class="cpv-video-placeholder">
          <i class="mdi mdi-video-off-outline" style="font-size:36px;"></i>
          <p class="mt-1 mb-0 small">No video loaded</p>
        </div>

        <!-- Masks darken everything outside the 9:16 crop region -->
        <div v-show="!!videoContentArea" :style="videoContentArea || {}">
          <div class="cpv-mask cpv-mask-l" ref="maskL"></div>
          <div class="cpv-mask cpv-mask-r" ref="maskR"></div>
          <div class="cpv-crop-box" ref="cropBox"></div>
        </div>
      </div>

      <!-- ── Scrub bar ── -->
      <div class="mb-2">
        <input
          ref="scrubSlider"
          type="range"
          class="form-range"
          :min="cutStart"
          :max="cutEnd"
          step="0.01"
          @input="onScrubInput"
          @change="onScrubChange"
        />
        <div class="d-flex justify-content-between" style="font-size:10px; color:#6c757d; font-family:monospace; margin-top:-4px;">
          <span>{{ formatSecs(cutStart) }}</span>
          <span>{{ formatSecs((cutStart + cutEnd) / 2) }}</span>
          <span>{{ formatSecs(cutEnd) }}</span>
        </div>
      </div>

      <!-- ── Controls row ── -->
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <button
          class="btn btn-sm"
          :class="isPlaying ? 'btn-warning' : 'btn-primary'"
          :disabled="!videoUrl"
          @click="isPlaying ? stopPreview() : playPreview()"
        >
          <i :class="isPlaying ? 'mdi mdi-stop' : 'mdi mdi-play'"></i>
          {{ isPlaying ? 'Stop' : 'Play Cut' }}
        </button>

        <!-- Speed selector -->
        <div class="btn-group btn-group-sm" role="group">
          <button
            v-for="s in [0.5, 1, 1.5]"
            :key="s"
            class="btn"
            :class="playbackSpeed === s ? 'btn-primary' : 'btn-outline-secondary'"
            @click="setPlaybackSpeed(s)"
          >{{ s }}×</button>
        </div>

        <span class="small text-muted font-monospace">
          {{ formatSecsDecimal(relativeTime) }} / {{ formatSecs(cutDuration) }}
        </span>
        <span class="small text-muted ms-auto">
          <i class="mdi mdi-information-outline"></i>
          Darkened areas are cropped out of the final 9:16 clip.
        </span>
      </div>

    </template>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="close">Close</b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.cpv-video-wrap {
  position: relative;
  width: fit-content;      /* hug the video so there are no letterbox bars */
  max-width: 100%;
  margin: 0 auto;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  line-height: 0;
}
.cpv-video {
  display: block;
  width: auto;             /* size by aspect ratio from the height */
  height: auto;
  max-width: 100%;
  max-height: 620px;
  object-fit: contain;
  background: #000;
}
.cpv-video-placeholder {
  width: 640px;
  max-width: 100%;
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  background: #1a1a2e;
  line-height: 1.4;
}

/* Crop preview masks — darken areas outside the 9:16 region */
.cpv-mask {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.72);
  pointer-events: none;
  transition: left 0.08s linear, width 0.08s linear;
}
.cpv-mask-l { left: 0; right: auto; }
.cpv-mask-r { right: 0; }

.cpv-crop-box {
  position: absolute;
  top: 0; bottom: 0;
  width: 31.640625%;
  border-left:  2px solid #f0c040;
  border-right: 2px solid #f0c040;
  pointer-events: none;
  transition: left 0.08s linear;
}
</style>
